import { TW_CheckboxCell_not_selected, TW_CheckboxCell_selected, TW_CheckboxCell_Wrapper } from "@utils/twClasses";
import { CheckSquare, Square } from "lucide-react";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { CellPosition, CellProps } from "./GridCell";
import { downPosition, leftPosition, nextPosition, prevPosition, rightPosition, upPosition } from "@utils/types";
import { useDataStore, useModelStore, useUserStore } from "src/store";
import { EditRightAccess, hasGlobalRightAccess, ObjectRightType, ViewRightType } from "@dto/Users";
import { areItemsEqual } from "@dto/records";


export const CheckboxCell = memo(function CheckboxCell({ item, position, updateValue, isEditing, isFocused }: CellProps) {

    // const edit = useDataStore(state => state.edit);
    const inputRef = useRef<HTMLDivElement>(null);
    const colNumber = useDataStore(state => state.colNumber);
    const focus = useDataStore(state => state.focus);
    // Only a real boolean is a checked box (a formula in error is rendered by FormulaErrorCell)
    const [value, setValue] = useState(item?.value === true);
    const wsColor = useModelStore(state => state.wsColor);
    const user = useUserStore(state => state.user);
    const userRole = useModelStore(state => state.userRole);
    const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
    const selectedView = useModelStore(state => state.selectedView);
    const hasEditAccessOnObject = hasGlobalRightAccess(user, userRole, ObjectRightType, EditRightAccess, selectedObjectInfos?.object?._key);
    const hasEditAccessOnView = hasGlobalRightAccess(user, userRole, ViewRightType, EditRightAccess, selectedView?._key);
    const hasEditAccess = hasEditAccessOnObject && hasEditAccessOnView;
    const readOnly = item.readOnly || !hasEditAccess;

    useEffect(() => {
        if (item?.value !== value) {
            setValue(item?.value === true);
        }
    }, [item]);

    useEffect(() => {
        if (isFocused) {
            setTimeout(() => {
                focusDiv();
            }, 100);
        }
    }, [isFocused, value]);

    const focusDiv = () => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    function validate(newValue: boolean) {
        const newItem = { ...item, value: newValue };
        updateValue(newItem, newValue);
    }

    const handleArrowKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, position: CellPosition | null) => {
        if (!isEditing || !position) {
            e.preventDefault();
            var newPosition = position;
            switch (e.key) {
                case 'ArrowUp':
                    newPosition = upPosition(position);
                    break;
                case 'ArrowDown':
                    newPosition = downPosition(position);
                    break;
                case 'ArrowLeft':
                    newPosition = leftPosition(position, colNumber);
                    break;
                case 'ArrowRight':
                    newPosition = rightPosition(position, colNumber);
                    break;
                default:
                    return; // Ignore other keys
            }
            focus(newPosition);
        }
    }

    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!position) {
            return;
        }
        // Treat arrow keys in separated function
        if (event.key.startsWith("Arrow")) {
            handleArrowKeyDown(event, position);
            return;
        }
        switch (event.key) {
            case "Enter":
                event.preventDefault();
                if (readOnly) {
                    break;
                }
                const newValue = !value;
                setValue(newValue);
                validate(newValue);

                break;
            case "Tab":
                // event.preventDefault();
                let next = null;
                if (event.shiftKey) {
                    next = prevPosition(position, colNumber);
                } else {
                    next = nextPosition(position, colNumber);
                }
                focus(next);
                break;
            case "Escape":
                event.preventDefault();
                focus(position);
                break;
            default:
                // Copy/paste (Ctrl+C / Ctrl+V) bubble up to GridCell, which
                // handles them for the whole selection.
                return;
        }
    };


    function onClick(event: React.MouseEvent<SVGSVGElement>) {
        event.preventDefault();
        if (readOnly) {
            return;
        }
        const newValue = !value;
        setValue(newValue);
        validate(newValue);
    }


    const selectedStyle = useMemo(() => {
        if (readOnly) {
            return {
                color: wsColor,
                cursor: "not-allowed",
                // opacity: 0.5
            };
        }
        return {
            color: wsColor,
            cursor: "pointer",
            // opacity: 1
        };
    }, [item])

    const notSelectedStyle = useMemo(() => {
        if (readOnly) {
            return {
                cursor: "not-allowed",
                // opacity: 0.5
            };
        }
        return {
            cursor: "pointer",
            // opacity: 1
        };
    }, [item])


    return (
        <div className={TW_CheckboxCell_Wrapper + (readOnly ? " cursor-not-allowed" : " cursor-pointer")} ref={inputRef} onKeyDown={onKeyDown} autoFocus={true} tabIndex={0}>
            {value ?
                <CheckSquare tabIndex={-1} className={TW_CheckboxCell_selected + (readOnly ? " cursor-not-allowed" : " cursor-pointer")} style={selectedStyle} aria-label="Check value" onClick={onClick} />
                :
                <Square tabIndex={-1} className={TW_CheckboxCell_not_selected + (readOnly ? " cursor-not-allowed" : " cursor-pointer")} style={notSelectedStyle} aria-label="Uncheck value" onClick={onClick} />
            }
        </div>
    )

}, (prevProps, newProps) => {
    // Optimized memo comparison - check most likely to change props first
    if (prevProps.position?.rowId !== newProps.position?.rowId ||
        prevProps.position?.column !== newProps.position?.column) {
        return false;
    }

    if (prevProps.isEditing !== newProps.isEditing) {
        return false;
    }

    if (prevProps.isFocused !== newProps.isFocused) {
        return false;
    }

    if (prevProps.currentAggregation !== newProps.currentAggregation) {
        return false;
    }

    if (prevProps.field !== newProps.field) {
        return false;
    }

    // For items, compare the key and value instead of object reference
    const itemEqual = areItemsEqual(prevProps.item, newProps.item);
    if (!itemEqual) {
        return false;
    }

    return true;
});
